import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { STATES, fmt, pctShares } from './signupReportUtils';

const KPI = ({ value, label, good }) => (
  <Box
    sx={{
      borderRadius: '12px',
      px: 2.2,
      py: 1.4,
      textAlign: 'center',
      minWidth: 130,
      bgcolor: good ? '#F0FDF4' : '#FFFBEB',
    }}
  >
    <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.5px', color: good ? '#16A34A' : '#F59E0B' }}>
      {value}%
    </Typography>
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', color: '#737373', mt: '2px' }}>
      {label}
    </Typography>
  </Box>
);

const SignupSummary = ({ totals, periodLabel }) => {
  const signed = totals.signed || 0;
  // Largest-remainder percentages for the seven disjoint statuses so the status bar
  // and legend always add up to exactly 100% (no rounding 99%/101% artifact).
  const shares = pctShares(
    STATES.reduce((acc, [key]) => ({ ...acc, [key]: totals[key] || 0 }), {}),
    signed
  );
  // KPIs read from the same shares so the headline numbers match the legend.
  // Active is not a segment: it is Approved + Hidden, so its % is the sum of those
  // two shares (keeps it equal to what the legend adds up to).
  const activePct = (shares.approved || 0) + (shares.hidden || 0);
  const approvedPct = shares.approved || 0;
  const ghostPct = shares.ghost || 0;

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #EAEAEA', borderRadius: '16px', p: { xs: 2, md: 3 }, mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2.5,
          flexWrap: 'wrap',
          mb: 2.5,
        }}
      >
        <Box sx={{ fontSize: 15, color: '#737373', fontWeight: 500 }}>
          <Box component="span" sx={{ display: 'block', fontSize: 30, fontWeight: 800, letterSpacing: '-1px', color: '#171717' }}>
            <Box component="span" sx={{ fontSize: 34, color: '#EF4444' }}>{fmt(signed)}</Box> people signed up
          </Box>
          <span>{periodLabel}</span> — here&apos;s where they are today.
        </Box>
        <Stack direction="row" spacing={1.5}>
          <KPI value={activePct} label="Active" good />
          <KPI value={approvedPct} label="Approved" good />
          <KPI value={ghostPct} label="Ghost" />
        </Stack>
      </Box>

      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', color: '#737373', mb: 1.2 }}>
        Where these signups are now
      </Typography>

      {/* Segmented composition bar. The seven statuses are mutually exclusive and
          sum to Signed Up, so each segment's width is its true share of signups
          (no normalization needed) and the bar fills the whole track. */}
      <Box sx={{ display: 'flex', height: 42, borderRadius: '10px', overflow: 'hidden', gap: '2px', mb: 1.5 }}>
        {STATES.map(([key, name, col]) => {
          const p = shares[key] || 0;
          return (
            <Box
              key={key}
              title={`${name}: ${fmt(totals[key])} (${p}%)`}
              sx={{
                width: `${p}%`,
                minWidth: totals[key] ? 2 : 0,
                bgcolor: col,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                transition: 'width .4s',
              }}
            >
              {p >= 8 && (
                <>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1 }}>{p}%</Typography>
                  <Typography sx={{ fontSize: 9, fontWeight: 600, opacity: 0.9, mt: '2px' }}>{name}</Typography>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 2.5 }, mt: 0.5 }}>
        {STATES.map(([key, name, col]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.9, fontSize: 13, color: '#404040' }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: col }} />
            <b style={{ fontSize: 15 }}>{fmt(totals[key])}</b> {name}{' '}
            <Box component="span" sx={{ color: '#A3A3A3' }}>({shares[key] || 0}%)</Box>
          </Box>
        ))}
      </Box>

      <Typography sx={{ fontSize: 11, color: '#A3A3A3', fontStyle: 'italic', mt: 1.5 }}>
        Everyone is counted on the day their profile was created and shown in their latest current state. Each signup
        sits in exactly one status (Approved, Hidden, Inactive, Ghost, Deactivated, Suspended, Deleted), so the statuses
        always sum to Signed Up — and re-bucket on the day a profile is hidden, goes inactive, is deleted, deactivated,
        or suspended. Active = Approved + Hidden, so it is shown as a figure above, not as a slice of the bar.
      </Typography>
    </Box>
  );
};

export default SignupSummary;
